import React, { useState } from 'react';
import { buildShareUrl } from '../utils/shareLinks';
import { currencySymbol } from '../utils/currency';
import { phoneNumberForWhatsApp } from '../utils/phoneNumbers';

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const TrashIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v5" />
    <path d="M14 11v5" />
  </svg>
);

const CopyIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <rect height="14" rx="2" ry="2" width="14" x="8" y="8" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const DuplicateIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <rect height="13" rx="2" ry="2" width="13" x="8" y="8" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    <path d="M15 12v5" />
    <path d="M12.5 14.5h5" />
  </svg>
);

const statusOptions = ['Draft', 'Active', 'Paused', 'Archived'];
const orderStatusOptions = ['Pending', 'Confirmed', 'Fulfilled', 'Cancelled'];
const receivingRecordStatusOptions = ['Pending', 'Confirmed', 'Cancelled'];
const receivingStatusOptions = ['Received', 'Partially received', 'Damaged', 'Back ordered', 'Not received'];
const pageSizeOptions = [5, 10, 20];
const submissionFilterOptions = [
  { id: 'all', label: 'All' },
  { id: 'responses', label: 'Responses' },
  { id: 'interactions', label: 'Interactions' },
  { id: 'itemForms', label: 'Item forms' },
  { id: 'orders', label: 'Orders' },
  { id: 'sales', label: 'Sales' },
];
const seenResponsesStorageKey = 'sharabl:seen-form-responses';
const submissionKey = (submission) => `${submission.type}:${submission.id}`;

const formatSubmittedAt = (value) => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatAnswer = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') {
    if (value.downloadUrl) {
      return (
        <a
          className="font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-950"
          href={value.downloadUrl}
          rel="noreferrer"
          target="_blank"
        >
          {value.name || 'Open uploaded file'}
        </a>
      );
    }
    return JSON.stringify(value);
  }
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const isImageAsset = (asset = {}) => {
  const contentType = asset.contentType || '';
  const url = asset.downloadUrl || asset.preview || '';

  return asset.type === 'Image' ||
    contentType.startsWith('image/') ||
    /\.(avif|gif|jpe?g|png|svg|webp)(\?|#|$)/i.test(url);
};

const getAssetImageUrl = (asset = {}) => {
  const directImage = asset.preview || asset.downloadUrl || asset.iconUrl || asset.listingLogoUrl || '';
  if (directImage && (isImageAsset(asset) || asset.iconUrl || asset.listingLogoUrl)) return directImage;

  const galleryImage = Array.isArray(asset.images)
    ? asset.images.find((image) => image.url || image.downloadUrl || image.preview)
    : null;

  return galleryImage?.url || galleryImage?.downloadUrl || galleryImage?.preview || '';
};

const formatCurrency = (currency, value) => {
  const amount = Number(value) || 0;
  return `${currencySymbol(currency)}${amount.toFixed(2)}`;
};

const firstFilledValue = (...values) => (
  values.find((value) => value !== undefined && value !== null && value !== '') ?? ''
);

const formatProfileLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatUpdateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatOrderNumber = (order) => {
  const value = order.orderNumber || order.id || '';
  const label = order.source === 'pos' ? 'Sale' : 'Order';
  if (!value) return label;
  return `${label} #${String(value).slice(0, 8).toUpperCase()}`;
};

const normaliseOrderStatus = (status) => (
  orderStatusOptions.includes(status) ? status : 'Pending'
);

const buildOrderUpdateMessage = (order, updateMessage) => [
  `${formatOrderNumber(order)} update`,
  '',
  updateMessage,
].join('\n');

const formatUpdateAuthor = (update) => (
  [update.authorName, update.authorRole].filter(Boolean).join(' | ')
);

const readSeenResponseIds = () => {
  try {
    return JSON.parse(window.localStorage.getItem(seenResponsesStorageKey) || '[]');
  } catch (error) {
    return [];
  }
};

function LinkTable({
  links,
  activeLinkId,
  formResponses = [],
  isCreating = false,
  itemFormResponses = [],
  orders = [],
  interactions = [],
  user,
  onDelete,
  onDuplicate,
  onItemFormResponseChange,
  onOrderChange,
  onSelect,
  onStatusChange,
  onSubmissionDelete,
}) {
  const [copiedLinkId, setCopiedLinkId] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [responseLinkId, setResponseLinkId] = useState('');
  const [submissionFilter, setSubmissionFilter] = useState('all');
  const [internalUpdateDrafts, setInternalUpdateDrafts] = useState({});
  const [orderUpdateDrafts, setOrderUpdateDrafts] = useState({});
  const [receivingDrafts, setReceivingDrafts] = useState({});
  const [seenResponseIds, setSeenResponseIds] = useState(readSeenResponseIds);
  const [openedNewResponseIds, setOpenedNewResponseIds] = useState([]);

  const filteredLinks = links.filter((link) => {
    const searchValue = query.trim().toLowerCase();
    if (!searchValue) return true;

    return [
      link.title,
      buildShareUrl(link),
      link.protection,
      link.status,
      link.category,
      link.owner,
    ].some((value) => String(value || '').toLowerCase().includes(searchValue));
  });
  const totalPages = Math.max(Math.ceil(filteredLinks.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const pagedLinks = filteredLinks.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firstItemIndex = filteredLinks.length ? (currentPage - 1) * pageSize + 1 : 0;
  const lastItemIndex = Math.min(currentPage * pageSize, filteredLinks.length);

  const handleSearchChange = (event) => {
    setQuery(event.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleCopyShareUrl = async (event, link) => {
    event.stopPropagation();
    await copyToClipboard(buildShareUrl(link));
    setCopiedLinkId(link.id);
    window.setTimeout(() => {
      setCopiedLinkId((currentId) => (currentId === link.id ? '' : currentId));
    }, 1800);
  };

  const handleDeleteLink = (event, link) => {
    event.stopPropagation();
    const confirmed = window.confirm(`Delete "${link.title || 'this link'}"? This cannot be undone.`);
    if (!confirmed) return;
    onDelete?.(link);
  };

  const handleDuplicateLink = (event, link) => {
    event.stopPropagation();
    onDuplicate?.(link);
  };

  const handleStatusChange = (event, link) => {
    event.stopPropagation();
    onStatusChange?.(link, event.target.value);
  };

  const handleOrderStatusChange = (event, order) => {
    onOrderChange?.(order, { status: event.target.value });
  };

  const handlePaymentStatusToggle = (order) => {
    const nextIsPaid = !(order.isPaid || order.paymentStatus === 'Paid');
    onOrderChange?.(order, {
      isPaid: nextIsPaid,
      paymentStatus: nextIsPaid ? 'Paid' : 'Unpaid',
    });
  };

  const handleSubmissionDelete = (submission) => {
    const label = submission.type === 'order'
      ? formatOrderNumber(submission)
      : submission.type === 'itemForm'
        ? submission.formTitle || 'this item form record'
        : 'this response';

    const confirmed = window.confirm(`Remove ${label}? This cannot be undone.`);
    if (!confirmed) return;

    onSubmissionDelete?.(submission);
  };

  const handleOrderUpdateDraftChange = (orderId, value) => {
    setOrderUpdateDrafts((currentDrafts) => ({
      ...currentDrafts,
      [orderId]: value,
    }));
  };

  const handleInternalUpdateDraftChange = (responseId, value) => {
    setInternalUpdateDrafts((currentDrafts) => ({
      ...currentDrafts,
      [responseId]: value,
    }));
  };

  const buildUpdateEntry = (message) => ({
    id: `update-${Date.now()}`,
    message: message.trim(),
    createdAt: new Date().toISOString(),
    authorId: user?.uid || '',
    authorName: user?.displayName || user?.profile?.displayName || user?.email || 'Internal user',
    authorRole: formatProfileLabel(user?.profile?.organisationRole || user?.profile?.role || user?.role || ''),
  });

  const handleAddOrderUpdate = (order) => {
    const message = (orderUpdateDrafts[order.id] || '').trim();
    if (!message) return;

    const nextUpdates = [
      buildUpdateEntry(message),
      ...(order.updates || []),
    ];

    onOrderChange?.(order, { updates: nextUpdates });
    setOrderUpdateDrafts((currentDrafts) => ({
      ...currentDrafts,
      [order.id]: '',
    }));
  };

  const getOrderUpdateDraft = (orderId) => orderUpdateDrafts[orderId] || '';

  const handleAddInternalUpdate = (response) => {
    const message = (internalUpdateDrafts[response.id] || '').trim();
    if (!message) return;

    const nextUpdates = [
      buildUpdateEntry(message),
      ...(response.updates || []),
    ];

    onItemFormResponseChange?.(response, { updates: nextUpdates });
    setInternalUpdateDrafts((currentDrafts) => ({
      ...currentDrafts,
      [response.id]: '',
    }));
  };

  const getInternalUpdateDraft = (responseId) => internalUpdateDrafts[responseId] || '';

  const isOrderingSubmission = (submission = {}) => submission.preset === 'ordering';
  const orderingSubmissionTotal = (submission = {}) => (
    Number(submission.orderTotal) ||
    (submission.items || []).reduce((sum, item) => (
      sum + (Number(item.fields?.itemTotal) || ((Number(item.fields?.unitCost) || 0) * (Number(item.fields?.orderedQuantity) || 0)))
    ), 0)
  );
  const orderingSubmissionOrderFields = (submission = {}) => {
    const firstItemFields = submission.items?.[0]?.fields || {};
    return {
      requiredBy: firstFilledValue(submission.orderFields?.requiredBy, firstItemFields.requiredBy),
      status: firstFilledValue(submission.orderFields?.status, firstItemFields.status),
      notes: firstFilledValue(submission.orderFields?.notes, firstItemFields.notes),
    };
  };

  const receivingItemKey = (item = {}, index = 0) => item.itemId || `${item.itemTitle || 'item'}-${index}`;

  const receivingItemsByKey = (response = {}) => Object.fromEntries((response.receiving?.items || []).map((item, index) => [
    receivingItemKey(item, index),
    item,
  ]));

  const normaliseReceivingRecordStatus = (status) => (
    receivingRecordStatusOptions.includes(status) ? status : 'Pending'
  );

  const receivingRecordStatusChoices = (status) => {
    const currentStatus = normaliseReceivingRecordStatus(status);
    if (currentStatus === 'Confirmed') return ['Confirmed', 'Cancelled'];
    if (currentStatus === 'Cancelled') return ['Cancelled', 'Confirmed'];
    return receivingRecordStatusOptions;
  };

  const getReceivingValue = (response, item, index, fieldId) => {
    const key = receivingItemKey(item, index);
    const draftItem = receivingDrafts[response.id]?.[key];
    if (draftItem && Object.prototype.hasOwnProperty.call(draftItem, fieldId)) {
      return draftItem[fieldId];
    }

    return receivingItemsByKey(response)[key]?.[fieldId] || '';
  };

  const handleReceivingDraftChange = (responseId, itemKey, fieldId, value) => {
    setReceivingDrafts((currentDrafts) => ({
      ...currentDrafts,
      [responseId]: {
        ...(currentDrafts[responseId] || {}),
        [itemKey]: {
          ...(currentDrafts[responseId]?.[itemKey] || {}),
          [fieldId]: value,
        },
      },
    }));
  };

  const buildReceivingItems = (response) => {
    const savedItems = receivingItemsByKey(response);
    const responseDrafts = receivingDrafts[response.id] || {};
    return (response.items || []).map((item, index) => {
      const key = receivingItemKey(item, index);
      return {
        itemId: item.itemId || '',
        itemTitle: item.itemTitle || '',
        receivedQuantity: '',
        status: '',
        receivedDate: '',
        notes: '',
        ...(savedItems[key] || {}),
        ...(responseDrafts[key] || {}),
        updatedAt: new Date().toISOString(),
      };
    });
  };

  const handleSaveReceiving = (response) => {
    onItemFormResponseChange?.(response, {
      receiving: {
        ...(response.receiving || {}),
        status: normaliseReceivingRecordStatus(response.receiving?.status),
        items: buildReceivingItems(response),
        updatedAt: new Date().toISOString(),
      },
    });
    setReceivingDrafts((currentDrafts) => ({
      ...currentDrafts,
      [response.id]: {},
    }));
  };

  const handleReceivingRecordStatusChange = (response, status) => {
    onItemFormResponseChange?.(response, {
      receiving: {
        ...(response.receiving || {}),
        status,
        items: buildReceivingItems(response),
        updatedAt: new Date().toISOString(),
      },
    });
    setReceivingDrafts((currentDrafts) => ({
      ...currentDrafts,
      [response.id]: {},
    }));
  };

  const hasReceivingDraft = (responseId) => Object.values(receivingDrafts[responseId] || {}).some((item) => (
    Object.keys(item || {}).length > 0
  ));

  const buildMailtoUrl = (order, update) => {
    const email = order.customer?.email || '';
    return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(`${formatOrderNumber(order)} update`)}&body=${encodeURIComponent(buildOrderUpdateMessage(order, update.message || ''))}`;
  };

  const buildWhatsAppUrl = (order, update) => {
    const phone = phoneNumberForWhatsApp(order.customer?.phone);
    return `https://wa.me/${phone}?text=${encodeURIComponent(buildOrderUpdateMessage(order, update.message || ''))}`;
  };

  const handleOrderUpdateSent = (order, updateId, channel) => {
    const nextUpdates = (order.updates || []).map((update) => (
      update.id === updateId
        ? {
          ...update,
          sentCount: (Number(update.sentCount) || 0) + 1,
          lastSentAt: new Date().toISOString(),
          lastSentChannel: channel,
        }
        : update
    ));

    onOrderChange?.(order, { updates: nextUpdates });
  };

  const responsesForLink = (linkId) => formResponses.filter((response) => response.linkId === linkId);
  const itemFormsForLink = (linkId) => itemFormResponses.filter((response) => response.linkId === linkId);
  const ordersForLink = (linkId) => orders.filter((order) => order.linkId === linkId);
  const interactionsForLink = (linkId) => interactions
    .filter((record) => record.linkId === linkId)
    .map((record) => ({
      ...record,
      type: 'interaction',
      submittedAt: record.submittedAt || record.createdAt || record.updatedAt,
    }));
  const submissionsForLink = (linkId) => [
    ...responsesForLink(linkId).map((response) => ({ ...response, type: 'response' })),
    ...interactionsForLink(linkId),
    ...itemFormsForLink(linkId).map((response) => ({ ...response, type: 'itemForm' })),
    ...ordersForLink(linkId).map((order) => ({ ...order, type: 'order' })),
  ].sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));

  const filterSubmissions = (submissions, filter) => submissions.filter((submission) => {
    if (filter === 'responses') return submission.type === 'response';
    if (filter === 'interactions') return submission.type === 'interaction';
    if (filter === 'itemForms') return submission.type === 'itemForm';
    if (filter === 'orders') return submission.type === 'order' && submission.source !== 'pos';
    if (filter === 'sales') return submission.type === 'order' && submission.source === 'pos';
    return true;
  });

  const canDuplicateLink = (link) => {
    const userId = user?.uid || '';
    const userRole = user?.profile?.role || user?.role || '';
    return Boolean(
      userId &&
      (
        link.ownerId === userId ||
        (Array.isArray(link.adminIds) && link.adminIds.includes(userId)) ||
        (Array.isArray(link.managerIds) && link.managerIds.includes(userId)) ||
        userRole === 'admin'
      ),
    );
  };

  const canOpenLink = (link) => Boolean(user?.uid && link?.ownerId === user.uid);

  const getFieldLabel = (link, fieldId, submission = {}) => {
    if (fieldId === 'bookingItems') return 'Accommodation';
    if (fieldId === 'bookingItemDetails') return 'Accommodation details';

    const assetForm = (link.assets || [])
      .find((asset) => asset.id === submission.assetId)
      ?.form;

    return assetForm?.fields?.find((field) => field.id === fieldId)?.label ||
      link.form?.fields?.find((field) => field.id === fieldId)?.label ||
      fieldId;
  };

  const getItemFormFieldLabel = (link, submission, fieldId) => {
    if (fieldId === 'storedQuantity') return 'Stored';
    if (fieldId === 'countedQuantity') return 'Counted';
    if (fieldId === 'quantityDifference') return 'Difference';
    const form = (link.itemForms || []).find((itemForm) => itemForm.id === submission.formId);
    return form?.itemFields?.find((field) => field.id === fieldId)?.label || fieldId;
  };

  const formatItemFormFieldValue = (link, submission, fieldId, value) => {
    const form = (link.itemForms || []).find((itemForm) => itemForm.id === submission.formId);
    const field = form?.itemFields?.find((itemField) => itemField.id === fieldId);
    return field?.type === 'currency'
      ? `${field.currencySymbol || 'R'}${formatAnswer(value)}`
      : formatAnswer(value);
  };

  const findSubmissionItemAsset = (item = {}) => {
    const assetId = item.assetId || item.itemId || '';
    const title = item.title || item.itemTitle || '';

    return (selectedResponseLink?.assets || []).find((asset) => (
      asset.id === assetId ||
      (!assetId && title && [asset.title, asset.name, asset.description].includes(title))
    ));
  };

  const renderSubmissionItemImage = (item) => {
    const asset = findSubmissionItemAsset(item);
    const imageUrl = item.imageUrl || item.preview || item.downloadUrl || getAssetImageUrl(asset);

    if (!imageUrl) return null;

    return (
      <img
        alt=""
        className="h-14 w-14 shrink-0 rounded border border-slate-200 bg-white object-cover"
        src={imageUrl}
      />
    );
  };

  const isSubmissionSeen = (submission) => (
    seenResponseIds.includes(submissionKey(submission)) ||
    (submission.type === 'response' && seenResponseIds.includes(submission.id))
  );

  const openResponses = (event, link) => {
    event.stopPropagation();
    const linkSubmissions = submissionsForLink(link.id);
    const newSubmissionIds = linkSubmissions
      .filter((submission) => !isSubmissionSeen(submission))
      .map(submissionKey);
    const nextSeenIds = Array.from(new Set([
      ...seenResponseIds,
      ...linkSubmissions.map(submissionKey),
    ]));

    setResponseLinkId(link.id);
    setSubmissionFilter('all');
    setOpenedNewResponseIds(newSubmissionIds);
    setSeenResponseIds(nextSeenIds);
    window.localStorage.setItem(seenResponsesStorageKey, JSON.stringify(nextSeenIds));
  };

  const selectedResponseLink = links.find((link) => link.id === responseLinkId);
  const selectedResponses = responseLinkId ? responsesForLink(responseLinkId) : [];
  const selectedInteractions = responseLinkId ? interactionsForLink(responseLinkId) : [];
  const selectedItemForms = responseLinkId ? itemFormsForLink(responseLinkId) : [];
  const selectedOrders = responseLinkId ? ordersForLink(responseLinkId) : [];
  const selectedAllSubmissions = responseLinkId ? submissionsForLink(responseLinkId) : [];
  const selectedSubmissions = filterSubmissions(selectedAllSubmissions, submissionFilter);
  const selectedSales = selectedOrders.filter((order) => order.source === 'pos');
  const selectedOnlineOrders = selectedOrders.filter((order) => order.source !== 'pos');
  const submissionFilterCounts = {
    all: selectedAllSubmissions.length,
    responses: selectedResponses.length,
    interactions: selectedInteractions.length,
    itemForms: selectedItemForms.length,
    orders: selectedOnlineOrders.length,
    sales: selectedSales.length,
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-4 py-3 md:flex-row md:items-center md:justify-between">
        <label className="min-w-0 flex-1 text-sm font-semibold text-slate-700" htmlFor="link-table-search">
          Search links
          <input
            className="field mt-2"
            id="link-table-search"
            onChange={handleSearchChange}
            placeholder="Search by name, URL, access, status..."
            value={query}
          />
        </label>
        <label className="text-sm font-semibold text-slate-700" htmlFor="link-table-page-size">
          Rows
          <select
            className="field mt-2 min-w-24"
            id="link-table-page-size"
            onChange={handlePageSizeChange}
            value={pageSize}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="link-table-scroll">
        <div className="grid table-row-header">
          <span>Name</span>
          <span className="hidden justify-self-center text-center md:block">Access</span>
          <span className="hidden justify-self-center text-center md:block">Assets</span>
          <span className="hidden justify-self-center text-center md:block">Views</span>
          <span className="hidden justify-self-center text-center md:block">Shares</span>
          <span className="justify-self-center text-center">Responses</span>
          <span className="justify-self-center text-center">Status</span>
          <span className="justify-self-center text-center">Duplicate</span>
          <span className="justify-self-center text-center">Delete</span>
        </div>
        {pagedLinks.map((link) => {
          const shareUrl = buildShareUrl(link);
          const isCopied = copiedLinkId === link.id;
          const openAllowed = canOpenLink(link);
          const duplicateAllowed = canDuplicateLink(link);
          const linkResponses = responsesForLink(link.id);
          const linkInteractions = interactionsForLink(link.id);
          const linkItemForms = itemFormsForLink(link.id);
          const linkOrders = ordersForLink(link.id);
          const linkSales = linkOrders.filter((order) => order.source === 'pos');
          const linkOnlineOrders = linkOrders.filter((order) => order.source !== 'pos');
          const linkSubmissions = submissionsForLink(link.id);
          const newResponseCount = linkSubmissions.filter((submission) => !isSubmissionSeen(submission)).length;

          return (
            <div
              className={activeLinkId === link.id && openAllowed ? 'grid table-row active' : 'grid table-row'}
              key={link.id}
            >
            <div className="flex min-w-0 items-start gap-2">
              <button
                className="min-w-0 flex-1 text-left disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!openAllowed}
                onClick={() => onSelect(link.id)}
                title={openAllowed ? 'Open link' : 'Duplicate this link first to open and edit your own copy'}
                type="button"
              >
                <strong className="block text-sm font-semibold text-slate-950">{link.title}</strong>
                <span className="block truncate text-xs text-slate-500">{shareUrl}</span>
              </button>
              <button
                aria-label={isCopied ? `Copied share URL for ${link.title || 'link'}` : `Copy share URL for ${link.title || 'link'}`}
                className="grid h-8 w-8 shrink-0 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                onClick={(event) => handleCopyShareUrl(event, link)}
                title={isCopied ? 'Copied' : 'Copy share URL'}
                type="button"
              >
                <CopyIcon />
              </button>
            </div>
            <button
              className="hidden justify-self-center text-center text-sm text-slate-600 disabled:cursor-not-allowed disabled:opacity-60 md:block"
              disabled={!openAllowed}
              onClick={() => onSelect(link.id)}
              title={openAllowed ? 'Open link' : 'Duplicate this link first to open and edit your own copy'}
              type="button"
            >
              {link.protection}
            </button>
            <button
              className="hidden justify-self-center text-center text-sm tabular-nums text-slate-600 disabled:cursor-not-allowed disabled:opacity-60 md:block"
              disabled={!openAllowed}
              onClick={() => onSelect(link.id)}
              title={openAllowed ? 'Open link' : 'Duplicate this link first to open and edit your own copy'}
              type="button"
            >
              {link.assets?.length || 0}
            </button>
            <button
              className="hidden justify-self-center text-center text-sm tabular-nums text-slate-600 disabled:cursor-not-allowed disabled:opacity-60 md:block"
              disabled={!openAllowed}
              onClick={() => onSelect(link.id)}
              title={openAllowed ? 'Open link' : 'Duplicate this link first to open and edit your own copy'}
              type="button"
            >
              {link.views || 0}
            </button>
            <button
              className="hidden justify-self-center text-center text-sm tabular-nums text-slate-600 disabled:cursor-not-allowed disabled:opacity-60 md:block"
              disabled={!openAllowed}
              onClick={() => onSelect(link.id)}
              title={openAllowed ? 'Open link' : 'Duplicate this link first to open and edit your own copy'}
              type="button"
            >
              {link.shares || 0}
            </button>
            <button
              className="justify-self-center rounded border border-slate-300 bg-white px-3 py-2 text-center text-xs font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!openAllowed}
              onClick={(event) => openResponses(event, link)}
              title={openAllowed ? 'Open responses' : 'Duplicate this link first to open and manage your own copy'}
              type="button"
            >
              {linkSubmissions.length}
              {newResponseCount > 0 && (
                <span className="ml-2 rounded bg-blue-600 px-1.5 py-0.5 text-[0.625rem] font-bold text-white">
                  {newResponseCount} new
                </span>
              )}
              {(linkResponses.length > 0 || linkInteractions.length > 0 || linkItemForms.length > 0 || linkOnlineOrders.length > 0 || linkSales.length > 0) && (
                <span className="mt-1 block text-[0.625rem] font-semibold text-slate-500">
                  {linkResponses.length} forms / {linkInteractions.length} interactions / {linkItemForms.length} item forms / {linkOnlineOrders.length} orders / {linkSales.length} sales
                </span>
              )}
            </button>
            <select
              aria-label={`Update status for ${link.title || 'link'}`}
              className="min-w-0 justify-self-center rounded border border-slate-200 bg-slate-50 px-2 py-1 text-center text-xs font-semibold uppercase text-slate-600 outline-none transition hover:border-slate-950 focus:border-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!openAllowed}
              onChange={(event) => handleStatusChange(event, link)}
              onClick={(event) => event.stopPropagation()}
              title={openAllowed ? 'Update status' : 'Duplicate this link first to manage your own copy'}
              value={link.status || 'Draft'}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <button
              aria-label={`Duplicate ${link.title || 'link'}`}
              className="grid h-9 w-9 place-items-center justify-self-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!duplicateAllowed || isCreating}
              onClick={(event) => handleDuplicateLink(event, link)}
              title={duplicateAllowed ? 'Duplicate link' : 'Only link owners, admins, or managers can duplicate this link'}
              type="button"
            >
              <DuplicateIcon />
            </button>
            <button
              aria-label={`Delete ${link.title || 'link'}`}
              className="grid h-9 w-9 place-items-center justify-self-center rounded border border-red-200 bg-red-50 text-red-700 transition hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!openAllowed}
              onClick={(event) => handleDeleteLink(event, link)}
              title={openAllowed ? 'Delete link' : 'Duplicate this link first to manage your own copy'}
              type="button"
            >
              <TrashIcon />
            </button>
            </div>
          );
        })}
        {!pagedLinks.length && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No links match your search.
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 md:flex-row md:items-center md:justify-between">
        <span>
          Showing {firstItemIndex}-{lastItemIndex} of {filteredLinks.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={currentPage <= 1}
            onClick={() => setPage((current) => Math.max(current - 1, 1))}
            type="button"
          >
            Previous
          </button>
          <span className="text-sm font-semibold text-slate-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
            type="button"
          >
            Next
          </button>
        </div>
      </div>
      {selectedResponseLink && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/40"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setResponseLinkId('');
          }}
          role="presentation"
        >
          <aside
            aria-labelledby="responses-title"
            aria-modal="true"
            className="ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
            role="dialog"
          >
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4">
              <div className="min-w-0">
                <p className="label-caps">Responses, interactions, orders, and sales</p>
                <h2 className="mt-1 truncate text-lg font-semibold text-slate-950" id="responses-title">
                  {selectedResponseLink.title}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedAllSubmissions.length} total submissions ({selectedResponses.length} forms / {selectedInteractions.length} interactions / {selectedItemForms.length} item forms / {selectedOnlineOrders.length} orders / {selectedSales.length} sales)
                </p>
              </div>
              <button
                aria-label="Close responses"
                className="icon-button"
                onClick={() => setResponseLinkId('')}
                type="button"
              >
                x
              </button>
            </div>

            <div className="grid gap-4 p-5">
              <div className="grid grid-cols-2 gap-2 rounded border border-slate-200 bg-slate-50 p-1 sm:grid-cols-4">
                {submissionFilterOptions.map((option) => (
                  <button
                    className={`rounded px-3 py-2 text-sm font-semibold transition ${
                      submissionFilter === option.id
                        ? 'bg-slate-950 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-950'
                    }`}
                    key={option.id}
                    onClick={() => setSubmissionFilter(option.id)}
                    type="button"
                  >
                    {option.label} ({submissionFilterCounts[option.id]})
                  </button>
                ))}
              </div>

              {!selectedAllSubmissions.length && (
                <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                  No form responses, interactions, orders, or sales have been submitted for this link yet.
                </div>
              )}
              {selectedAllSubmissions.length > 0 && !selectedSubmissions.length && (
                <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                  No submissions match this filter.
                </div>
              )}
              {selectedSubmissions.map((submission) => {
                const isPosOrder = submission.type === 'order' && submission.source === 'pos';
                const isPaidOrder = submission.isPaid || submission.paymentStatus === 'Paid';

                return (
                <article
                  className={`rounded border bg-white p-4 ${isPosOrder ? 'border-emerald-200' : 'border-slate-200'}`}
                  key={submissionKey(submission)}
                >
                  <div className="mb-3 flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-slate-950">
                          {submission.type === 'order'
                            ? formatOrderNumber(submission)
                              : submission.type === 'itemForm'
                                ? (submission.formTitle || 'Item form')
                              : submission.type === 'interaction'
                                ? (submission.gridTitle || 'Interaction')
                                : submission.formTitle || 'Response'}
                        </p>
                        {submission.type === 'response' && submission.assetTitle && (
                          <span className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[0.625rem] font-semibold uppercase leading-none text-slate-600">
                            {submission.assetTitle}
                          </span>
                        )}
                        {submission.type === 'itemForm' && (
                          <span className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-[0.625rem] font-semibold uppercase leading-none text-blue-700">
                            Internal
                          </span>
                        )}
                        {submission.type === 'interaction' && (
                          <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[0.625rem] font-semibold uppercase leading-none text-amber-700">
                            Interaction
                          </span>
                        )}
                        {isPosOrder && (
                          <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[0.625rem] font-semibold uppercase leading-none text-emerald-700">
                            In-store sale
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{formatSubmittedAt(submission.submittedAt)}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      {submission.type === 'order' && (
                        <>
                          <span className="chip">{normaliseOrderStatus(submission.status)}</span>
                          <span className={`chip ${isPaidOrder ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                            {submission.paymentStatus || (submission.isPaid ? 'Paid' : 'Unpaid')}
                          </span>
                        </>
                      )}
                      {openedNewResponseIds.includes(submissionKey(submission)) && <span className="chip">New</span>}
                      {submission.type !== 'interaction' && (
                        <button
                          aria-label="Remove record"
                          className="grid h-8 w-8 place-items-center rounded border border-red-200 bg-red-50 text-red-700 transition hover:border-red-300 hover:bg-red-100"
                          onClick={() => handleSubmissionDelete(submission)}
                          title="Remove record"
                          type="button"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </div>
                  {submission.type === 'order' ? (
                    <div className="grid gap-4">
                      <div className="grid gap-3 rounded border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div>
                          <label className="field-label mt-0" htmlFor={`order-status-${submission.id}`}>
                            {isPosOrder ? 'Sale status' : 'Order status'}
                          </label>
                          <select
                            className="field"
                            id={`order-status-${submission.id}`}
                            onChange={(event) => handleOrderStatusChange(event, submission)}
                            value={normaliseOrderStatus(submission.status)}
                          >
                            {orderStatusOptions.map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          className={`rounded border px-4 py-3 text-sm font-semibold transition ${
                            isPaidOrder
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100'
                              : 'border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100'
                          }`}
                          onClick={() => handlePaymentStatusToggle(submission)}
                          type="button"
                        >
                          {isPaidOrder ? 'Mark unpaid' : 'Mark paid'}
                        </button>
                      </div>
                      <dl className="grid gap-3">
                        {submission.source === 'pos' && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-slate-500">Sale source</dt>
                            <dd className="mt-1 text-sm text-slate-950">In-store POS</dd>
                          </div>
                        )}
                        {submission.orderType && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-slate-500">Order type</dt>
                            <dd className="mt-1 text-sm text-slate-950">
                              {submission.orderType === 'onsite' ? 'Onsite' : 'Offsite'}
                            </dd>
                          </div>
                        )}
                        {submission.tableNumber && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-slate-500">Table number</dt>
                            <dd className="mt-1 text-sm text-slate-950">{formatAnswer(submission.tableNumber)}</dd>
                          </div>
                        )}
                        {submission.deliveryAddress && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-slate-500">Delivery address</dt>
                            <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-950">{formatAnswer(submission.deliveryAddress)}</dd>
                          </div>
                        )}
                        {submission.paymentMethod && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-slate-500">Payment type</dt>
                            <dd className="mt-1 text-sm text-slate-950">{formatAnswer(submission.paymentMethod)}</dd>
                          </div>
                        )}
                        <div>
                          <dt className="text-xs font-semibold uppercase text-slate-500">Customer</dt>
                          <dd className="mt-1 text-sm text-slate-950">{formatAnswer(submission.customer?.name)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-semibold uppercase text-slate-500">Email</dt>
                          <dd className="mt-1 break-all text-sm text-slate-950">{formatAnswer(submission.customer?.email)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-semibold uppercase text-slate-500">Phone</dt>
                          <dd className="mt-1 text-sm text-slate-950">{formatAnswer(submission.customer?.phone)}</dd>
                        </div>
                        {submission.customer?.notes && (
                          <div>
                            <dt className="text-xs font-semibold uppercase text-slate-500">Notes</dt>
                            <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-950">{submission.customer.notes}</dd>
                          </div>
                        )}
                      </dl>
                      <div className="rounded border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <p className="text-xs font-semibold uppercase text-slate-500">Items</p>
                          <p className="text-sm font-semibold text-slate-950">
                            {formatCurrency(submission.currency, submission.total)}
                          </p>
                        </div>
                        <div className="grid gap-2">
                          {(submission.items || []).map((item, index) => {
                            const itemImage = renderSubmissionItemImage(item);

                            return (
                            <div className="flex items-start justify-between gap-3 text-sm" key={`${item.assetId || item.title}-${index}`}>
                              <div className="flex min-w-0 items-start gap-3">
                                {itemImage}
                                <div className="min-w-0">
                                  <p className="font-semibold text-slate-950">{item.title || 'Untitled item'}</p>
                                  <p className="mt-0.5 text-xs text-slate-500">
                                    Qty {item.quantity} x {formatCurrency(item.currency || submission.currency, item.price)}
                                  </p>
                                  {(item.selectedSize || item.selectedColor) && (
                                    <p className="mt-0.5 text-xs text-slate-500">
                                      {[item.selectedSize && `Size: ${item.selectedSize}`, item.selectedColor && `Color: ${item.selectedColor}`]
                                        .filter(Boolean)
                                        .join(' | ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <p className="font-semibold text-slate-950">{formatCurrency(item.currency || submission.currency, item.lineTotal)}</p>
                            </div>
                            );
                          })}
                        </div>
                        {Number(submission.deliveryFee) > 0 && (
                          <div className="mt-3 grid gap-1 border-t border-slate-200 pt-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-semibold text-slate-600">Subtotal</span>
                              <span className="font-semibold text-slate-950">
                                {formatCurrency(submission.currency, submission.subtotal ?? (Number(submission.total) - Number(submission.deliveryFee)))}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-semibold text-slate-600">Delivery</span>
                              <span className="font-semibold text-slate-950">
                                {formatCurrency(submission.currency, submission.deliveryFee)}
                              </span>
                            </div>
                          </div>
                        )}
                        {(Number(submission.totalCost) > 0 || Number(submission.totalProfit) > 0) && (
                          <div className="mt-3 grid gap-1 border-t border-slate-200 pt-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-semibold text-slate-600">Total cost</span>
                              <span className="font-semibold text-slate-950">
                                {formatCurrency(submission.currency, submission.totalCost)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-semibold text-slate-600">Total profit</span>
                              <span className="font-semibold text-slate-950">
                                {formatCurrency(submission.currency, submission.totalProfit)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="grid gap-3 border-t border-slate-200 pt-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-950">{isPosOrder ? 'Sale updates' : 'Order updates'}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Save each update first, then send or resend it from the saved update row.
                          </p>
                        </div>
                        <textarea
                          className="field min-h-24 resize-none"
                          onChange={(event) => handleOrderUpdateDraftChange(submission.id, event.target.value)}
                          placeholder={isPosOrder ? 'Write a sale update' : 'Write an order update for the customer'}
                          value={getOrderUpdateDraft(submission.id)}
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            className="rounded bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                            disabled={!getOrderUpdateDraft(submission.id).trim()}
                            onClick={() => handleAddOrderUpdate(submission)}
                            type="button"
                          >
                            Add update
                          </button>
                        </div>
                        {(submission.updates || []).length > 0 && (
                          <div className="grid gap-2">
                            {(submission.updates || []).map((update) => (
                              <div className="rounded border border-slate-200 bg-slate-50 p-3" key={update.id || update.createdAt}>
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="whitespace-pre-wrap text-sm text-slate-950">{update.message}</p>
                                    <p className="mt-2 text-xs font-semibold text-slate-500">
                                      {formatUpdateAuthor(update) && `${formatUpdateAuthor(update)} | `}
                                      {formatUpdateTime(update.createdAt)}
                                      {update.lastSentAt && ` | Last sent ${formatUpdateTime(update.lastSentAt)}${update.lastSentChannel ? ` by ${update.lastSentChannel}` : ''}`}
                                    </p>
                                  </div>
                                  <div className="flex shrink-0 items-center gap-2">
                                    <span className="chip">Sent {Number(update.sentCount) || 0}</span>
                                    <a
                                      aria-label="Send update by email"
                                      className={`grid h-9 w-9 place-items-center rounded border border-slate-300 bg-white text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 ${!submission.customer?.email ? 'pointer-events-none opacity-50' : ''}`}
                                      href={buildMailtoUrl(submission, update)}
                                      onClick={() => handleOrderUpdateSent(submission, update.id, 'email')}
                                      title="Send by email"
                                    >
                                      @
                                    </a>
                                    <a
                                      aria-label="Send update by WhatsApp"
                                      className={`grid h-9 w-9 place-items-center rounded border border-slate-300 bg-white text-xs font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 ${!phoneNumberForWhatsApp(submission.customer?.phone) ? 'pointer-events-none opacity-50' : ''}`}
                                      href={buildWhatsAppUrl(submission, update)}
                                      onClick={() => handleOrderUpdateSent(submission, update.id, 'WhatsApp')}
                                      rel="noreferrer"
                                      target="_blank"
                                      title="Send by WhatsApp"
                                    >
                                      WA
                                    </a>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : submission.type === 'interaction' ? (
                    <div className="grid gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase text-slate-500">Grid</p>
                        <p className="mt-1 text-sm text-slate-950">{formatAnswer(submission.gridTitle)}</p>
                      </div>
                      <div className="grid gap-2">
                        {(submission.interactions || []).map((interaction, index) => (
                          <div
                            className="rounded border border-slate-200 bg-slate-50 p-3"
                            key={interaction.id || `${interaction.rowId}-${interaction.columnId}-${index}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-slate-950">{interaction.columnLabel || 'Interaction'}</p>
                              <span className="chip">{formatProfileLabel(interaction.interactionType)}</span>
                            </div>
                            <dl className="mt-3 grid gap-2">
                              <div>
                                <dt className="text-xs font-semibold uppercase text-slate-500">Value</dt>
                                <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-950">{formatAnswer(interaction.value)}</dd>
                              </div>
                              <div>
                                <dt className="text-xs font-semibold uppercase text-slate-500">Row</dt>
                                <dd className="mt-1 text-sm text-slate-950">{formatAnswer(interaction.rowLabel || interaction.rowId)}</dd>
                              </div>
                            </dl>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : submission.type === 'itemForm' ? (
                    <div className="grid gap-4">
                      {isOrderingSubmission(submission) && (
                        <dl className="grid gap-2 rounded border border-slate-200 bg-slate-50 p-3">
                          <div>
                            <dt className="text-xs font-semibold uppercase text-slate-500">Required by</dt>
                            <dd className="mt-1 text-sm text-slate-950">{formatAnswer(orderingSubmissionOrderFields(submission).requiredBy)}</dd>
                          </div>
                        </dl>
                      )}
                      <div className="grid gap-3">
                        {(submission.items || []).map((item, index) => {
                          const itemImage = renderSubmissionItemImage(item);

                          return (
                          <div className="rounded border border-slate-200 bg-slate-50 p-3" key={`${item.itemId || item.itemTitle}-${index}`}>
                            <div className="flex min-w-0 items-start gap-3">
                              {itemImage}
                              <p className="min-w-0 text-sm font-semibold text-slate-950">{item.itemTitle || 'Untitled item'}</p>
                            </div>
                            {submission.preset === 'stock-taking' && (
                              <div className="mt-3 grid grid-cols-3 gap-2">
                                <div className="rounded border border-slate-200 bg-white px-3 py-2">
                                  <p className="text-xs font-semibold uppercase text-slate-500">Stored</p>
                                  <p className="mt-1 text-sm font-semibold text-slate-950">{formatAnswer(item.fields?.storedQuantity)}</p>
                                </div>
                                <div className="rounded border border-slate-200 bg-white px-3 py-2">
                                  <p className="text-xs font-semibold uppercase text-slate-500">Counted</p>
                                  <p className="mt-1 text-sm font-semibold text-slate-950">
                                    {formatAnswer(firstFilledValue(item.fields?.countedQuantity, item.fields?.quantity))}
                                  </p>
                                </div>
                                <div className="rounded border border-slate-200 bg-white px-3 py-2">
                                  <p className="text-xs font-semibold uppercase text-slate-500">Difference</p>
                                  <p className={`mt-1 text-sm font-semibold ${Number(item.fields?.quantityDifference) < 0 ? 'text-red-700' : Number(item.fields?.quantityDifference) > 0 ? 'text-emerald-700' : 'text-slate-950'}`}>
                                    {formatAnswer(item.fields?.quantityDifference)}
                                  </p>
                                </div>
                              </div>
                            )}
                            <dl className="mt-3 grid gap-2">
                              {Object.entries(item.fields || {})
                                .filter(([fieldId]) => ![
                                  'storedQuantity',
                                  'countedQuantity',
                                  'quantityDifference',
                                  ...(isOrderingSubmission(submission) ? ['requiredBy', 'status', 'notes'] : []),
                                ].includes(fieldId))
                                .map(([fieldId, value]) => (
                                  <div key={fieldId}>
                                    <dt className="text-xs font-semibold uppercase text-slate-500">
                                      {getItemFormFieldLabel(selectedResponseLink, submission, fieldId)}
                                    </dt>
                                    <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-950">
                                      {formatItemFormFieldValue(selectedResponseLink, submission, fieldId, value)}
                                    </dd>
                                  </div>
                              ))}
                            </dl>
                          </div>
                          );
                        })}
                      </div>
                      {isOrderingSubmission(submission) && (
                        <div className="grid gap-3 rounded border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-600">Order total</span>
                            <strong className="text-lg font-semibold text-slate-950">
                              {formatCurrency(selectedResponseLink?.currency || 'ZAR', orderingSubmissionTotal(submission))}
                            </strong>
                          </div>
                          <dl className="grid gap-2">
                            <div>
                              <dt className="text-xs font-semibold uppercase text-slate-500">Status</dt>
                              <dd className="mt-1 text-sm text-slate-950">{formatAnswer(orderingSubmissionOrderFields(submission).status)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs font-semibold uppercase text-slate-500">Notes</dt>
                              <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-950">{formatAnswer(orderingSubmissionOrderFields(submission).notes)}</dd>
                            </div>
                          </dl>
                        </div>
                      )}
                      {isOrderingSubmission(submission) && (
                        <div className="grid gap-3 border-t border-slate-200 pt-4">
                          <div>
                            <p className="text-sm font-semibold text-slate-950">Receiving</p>
                            <p className="mt-1 text-xs text-slate-500">
                              Record received quantities and status against the ordered items.
                            </p>
                          </div>
                          <div className="grid gap-3 rounded border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_auto] sm:items-end">
                            <div>
                              <label className="field-label mt-0" htmlFor={`receiving-record-status-${submission.id}`}>Receiving status</label>
                              <select
                                className="field"
                                id={`receiving-record-status-${submission.id}`}
                                onChange={(event) => handleReceivingRecordStatusChange(submission, event.target.value)}
                                value={normaliseReceivingRecordStatus(submission.receiving?.status)}
                              >
                                {receivingRecordStatusChoices(submission.receiving?.status).map((status) => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                            </div>
                            <span className="chip justify-self-start">
                              {normaliseReceivingRecordStatus(submission.receiving?.status)}
                            </span>
                          </div>
                          <div className="grid gap-3">
                            {(submission.items || []).map((item, index) => {
                              const itemKey = receivingItemKey(item, index);
                              const isReceivingLocked = normaliseReceivingRecordStatus(submission.receiving?.status) === 'Confirmed';

                              return (
                                <div className="rounded border border-slate-200 bg-slate-50 p-3" key={`receiving-${itemKey}`}>
                                  <p className="text-sm font-semibold text-slate-950">{item.itemTitle || 'Untitled item'}</p>
                                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                    <div>
                                      <label className="field-label mt-0" htmlFor={`receiving-quantity-${submission.id}-${itemKey}`}>Received quantity</label>
                                      <input
                                        className="field"
                                        id={`receiving-quantity-${submission.id}-${itemKey}`}
                                        inputMode="decimal"
                                        disabled={isReceivingLocked}
                                        onChange={(event) => handleReceivingDraftChange(submission.id, itemKey, 'receivedQuantity', event.target.value)}
                                        placeholder="0"
                                        type="number"
                                        value={getReceivingValue(submission, item, index, 'receivedQuantity')}
                                      />
                                    </div>
                                    <div>
                                      <label className="field-label mt-0" htmlFor={`receiving-status-${submission.id}-${itemKey}`}>Status</label>
                                      <select
                                        className="field"
                                        disabled={isReceivingLocked}
                                        id={`receiving-status-${submission.id}-${itemKey}`}
                                        onChange={(event) => handleReceivingDraftChange(submission.id, itemKey, 'status', event.target.value)}
                                        value={getReceivingValue(submission, item, index, 'status')}
                                      >
                                        <option value="">Select status</option>
                                        {receivingStatusOptions.map((status) => (
                                          <option key={status} value={status}>{status}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="field-label mt-0" htmlFor={`receiving-date-${submission.id}-${itemKey}`}>Received date</label>
                                      <input
                                        className="field"
                                        disabled={isReceivingLocked}
                                        id={`receiving-date-${submission.id}-${itemKey}`}
                                        onChange={(event) => handleReceivingDraftChange(submission.id, itemKey, 'receivedDate', event.target.value)}
                                        type="date"
                                        value={getReceivingValue(submission, item, index, 'receivedDate')}
                                      />
                                    </div>
                                    <div className="sm:col-span-2">
                                      <label className="field-label mt-0" htmlFor={`receiving-notes-${submission.id}-${itemKey}`}>Notes</label>
                                      <textarea
                                        className="field min-h-20 resize-none"
                                        disabled={isReceivingLocked}
                                        id={`receiving-notes-${submission.id}-${itemKey}`}
                                        onChange={(event) => handleReceivingDraftChange(submission.id, itemKey, 'notes', event.target.value)}
                                        placeholder="Receiving details, condition, or follow-up"
                                        value={getReceivingValue(submission, item, index, 'notes')}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              className="rounded bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                              disabled={!hasReceivingDraft(submission.id) || normaliseReceivingRecordStatus(submission.receiving?.status) === 'Confirmed'}
                              onClick={() => handleSaveReceiving(submission)}
                              type="button"
                            >
                              Save receiving
                            </button>
                            {submission.receiving?.updatedAt && (
                              <p className="text-xs font-semibold text-slate-500">
                                Last updated {formatUpdateTime(submission.receiving.updatedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="grid gap-3 border-t border-slate-200 pt-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-950">Internal updates</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Add notes and follow-up updates for this internal record.
                          </p>
                        </div>
                        <textarea
                          className="field min-h-24 resize-none"
                          onChange={(event) => handleInternalUpdateDraftChange(submission.id, event.target.value)}
                          placeholder="Write an internal update"
                          value={getInternalUpdateDraft(submission.id)}
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            className="rounded bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                            disabled={!getInternalUpdateDraft(submission.id).trim()}
                            onClick={() => handleAddInternalUpdate(submission)}
                            type="button"
                          >
                            Add update
                          </button>
                        </div>
                        {(submission.updates || []).length > 0 && (
                          <div className="grid gap-2">
                            {(submission.updates || []).map((update) => (
                              <div className="rounded border border-slate-200 bg-slate-50 p-3" key={update.id || update.createdAt}>
                                <p className="whitespace-pre-wrap text-sm text-slate-950">{update.message}</p>
                                <p className="mt-2 text-xs font-semibold text-slate-500">
                                  {formatUpdateAuthor(update) && `${formatUpdateAuthor(update)} | `}
                                  {formatUpdateTime(update.createdAt)}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {Array.isArray(submission.answers?.bookingItemDetails) && submission.answers.bookingItemDetails.length > 0 && (
                        <div className="grid gap-3">
                          {submission.answers.bookingItemDetails.map((item, index) => {
                            const itemImage = renderSubmissionItemImage(item);
                            return (
                              <div className="rounded border border-slate-200 bg-slate-50 p-3" key={`${item.assetId || item.title}-${index}`}>
                                <div className="flex min-w-0 items-start gap-3">
                                  {itemImage}
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-slate-950">{item.title || 'Untitled accommodation'}</p>
                                    <p className="mt-1 text-xs text-slate-500">
                                      {Number.isFinite(Number(item.availableRooms))
                                        ? `${item.rooms} room${Number(item.rooms) === 1 ? '' : 's'} requested${Number.isFinite(Number(item.beds)) ? `, ${item.beds} bed${Number(item.beds) === 1 ? '' : 's'}` : ''} (${item.availableRooms} available)`
                                        : 'Service requested'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <dl className="grid gap-3">
                        {Object.entries(submission.answers || {})
                          .filter(([fieldId]) => fieldId !== 'bookingItemDetails')
                          .map(([fieldId, value]) => (
                        <div key={fieldId}>
                          <dt className="text-xs font-semibold uppercase text-slate-500">{getFieldLabel(selectedResponseLink, fieldId, submission)}</dt>
                          <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-950">{formatAnswer(value)}</dd>
                        </div>
                      ))}
                      </dl>
                    </div>
                  )}
                </article>
                );
              })}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default LinkTable;
